<template>
  <button
    v-if="isAddedToCart && cartStore.patients.length <= 1"
    :class="[
      {
        'cursor-not-allowed': isDisabled,
        active: !isDisabled,
      },
    ]"
    class="group flex h-[42px] w-[42px] flex-shrink-0 items-center justify-end rounded-full border border-form bg-blue p-px pl-2 transition-all hover:bg-white xs:w-[116px]"
    @click="!isDisabled && onClickRemoveFromCart()"
  >
    <span
      class="hidden w-full whitespace-nowrap px-1 text-sm font-medium text-white group-hover:text-blue xs:block"
    >
      {{ $t('referrals.added') }}
    </span>
    <span
      class="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-full bg-white text-success group-hover:bg-blue group-hover:text-white hover:bg-white hover:text-white"
    >
      <inline-svg :src="useAsset(`icons/base/check.svg`)" />
    </span>
  </button>

  <button
    v-else-if="isAddedToCart"
    :class="[
      {
        'cursor-not-allowed': isDisabled,
        active: !isDisabled,
      },
    ]"
    class="group relative flex h-[42px] w-[42px] flex-shrink-0 items-center rounded-full border border-form bg-blue p-px transition-all hover:bg-white xs:w-[116px]"
    @click="!isDisabled && onClickAddToCart()"
  >
    <div
      class="absolute -left-1 -top-2 flex h-5 w-5 items-center justify-center rounded-full border border-white bg-success text-s font-bold text-white"
    >
      {{ patientsWithEamination }}
    </div>
    <span
      class="hidden w-full whitespace-nowrap pl-2 pr-1 text-sm font-medium text-white group-hover:text-blue xs:block"
    >
      {{ $t('referrals.added') }}
    </span>
    <span
      class="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-full bg-white text-blue group-hover:bg-blue group-hover:text-white hover:bg-white"
    >
      <inline-svg :src="useAsset(`icons/base/check.svg`)" />
    </span>
  </button>

  <button
    v-else
    :class="[
      {
        'cursor-not-allowed opacity-50': isDisabled,
      },
    ]"
    class="group flex h-[42px] w-[42px] flex-shrink-0 items-center rounded-full border border-form bg-white p-px pr-2 transition-all hover:bg-blue xs:w-[116px]"
    @click="!isDisabled && onClickAddToCart()"
  >
    <span
      class="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-full bg-blue text-white group-hover:bg-white group-hover:text-blue hover:bg-blue"
    >
      <inline-svg :src="useAsset(`icons/base/plus.svg`)" />
    </span>
    <span
      class="hidden w-full whitespace-nowrap px-1 text-sm font-medium text-blue group-hover:text-white xs:block"
    >
      {{ $t('action.add') }}
    </span>
  </button>
</template>

<script setup>
import { useCartStore } from '~~/stores/cart'
import { useModalsStore } from '~~/stores/modals'
import InlineSvg from 'vue-inline-svg'
import { useAppStore } from '~/stores/app'
import { useAuthStore } from '~~/stores/auth'

const props = defineProps({
  item: {
    type: [Object],
    required: true,
  },
  size: {
    type: [String],
    default: '',
  },
  disabled: {
    type: [Boolean],
    default: false,
  },
})
const {
  public: { currency },
} = useRuntimeConfig()

const authStore = useAuthStore()
const cartStore = useCartStore()
const appStore = useAppStore()
const modalStore = useModalsStore()

const { t } = useI18n()
const gtm = useGtm()

const isAddedToCart = computed(() => {
  return cartStore.isAddedToCart(props.item?.id)
})

const isAddedByDoctor = computed(() => {
  return cartStore.isAddedByDoctor(props.item?.id)
})

const isDisabled = computed(() => {
  return (
    !appStore.isReturnedSharedInitData ||
    props.disabled ||
    cartStore.isAddingToCart ||
    isAddedByDoctor.value
  )
})

const discount = computed(() => {
  return cartStore.getDiscountForAll
})

const onClickAddToCart = () => {
  cartStore.isAddingToCart = true
  const item = props.item

  const examination = {
    id: item.id,
    service_id: item.service_id,
    name: item.name,
    slug: item.slug,
    price: item.price,
    package: item.package,
    require_nursing_services: item.require_nursing_services,
    require_covid_nursing_services: item.require_covid_nursing_services,
    available: true,
    added_by_doctor: false,
    upsell_services: item.upsell_services,
    upsell: item.upsell || false,
    patient_ids: item.patient_ids || '',
  }
  const patientsIds = cartStore.patients.map((patient) => patient.id)

  if (cartStore.patients.length > 1) {
    cartStore.selectedExamination = examination
    modalStore.open('addPatient')
  } else if (cartStore.discountCode) {
    modalStore.openModalCallback('confirmModal', {
      title: `<div class="text-center">${t('cart.attention')}</div>`,
      text: `<div class="text-primary text-center">${t(
        'cart.confirm_remove_discount_code'
      )}</div>`,
      onConfirm: () => {
        addToCart(examination, patientsIds)
        cartStore.removeDiscountCode()
      },
      onCancel: () => {
        cartStore.isAddingToCart = false
      },
    })
  } else {
    addToCart(examination, patientsIds)
  }
}

const addToCart = (examination, patients) => {
  if (cartStore.shouldBeSelectedPoint) {
    cartStore.tmpItem = { examination, patients }
    modalStore.open('selectCollectionPointToContinue')
  } else if (cartStore.shouldBeSelectedCity) {
    cartStore.tmpItem = { examination, patients }
    modalStore.open('chooseCityModal')
  } else if (cartStore.isAddedFullLipidogramPackage) {
    pushToCart(examination, patients)
    cartStore.replaceItemsWithLipidogramPackage(
      cartStore.patients.map((patient) => patient.id)
    )
  } else {
    pushToCart(examination, patients)
  }

  cartStore.isAddingToCart = false
}

const pushToCart = (examination, patientsIds) => {
  const { service_id, name, slug, price, package: is_package } = examination

  cartStore.pushToCart({ examination, patients: patientsIds ?? [] })

  gtm.trackEvent({ ecommerce: null })
  gtm.trackEvent({
    event: 'add_to_cart',
    ecommerce: {
      currency,
      value: price,
      items: [
        {
          id: service_id,
          name,
          slug,
          price,
          is_package,
        },
      ],
      email: authStore.user?.email,
      sha256_email: authStore.user?.sha256_email,
      md5_email: authStore.user?.md5_email,
    },
  })
}

const patientsWithEamination = computed(() => {
  return cartStore.getExaminationPatients(props.item.id).length
})

const onClickRemoveFromCart = () => {
  let title = ''
  let text = t('cart.confirm_remove_examination')

  modalStore.openModalCallback('confirmModal', {
    title: `<div class="text-center">${title}</div>`,
    text: `<div class="text-primary text-center">${text}</div>`,
    onConfirm: () => {
      removeFromCart()
    },
  })
}

const removeFromCart = () => {
  const {
    service_id,
    name,
    slug,
    price,
    package: is_package,
  } = cartStore.getItem(props.item.id)

  gtm.trackEvent({ ecommerce: null })
  gtm.trackEvent({
    event: 'remove_from_cart',
    ecommerce: {
      currency,
      value: price,
      items: [
        {
          id: service_id,
          name,
          slug,
          price,
          is_package,
        },
      ],
    },
  })

  cartStore.removeFromCart(props.item.id)
  cartStore.removeDiscountCode()
}

defineExpose({
  onClickAddToCart,
})
</script>
